import { Injectable } from '@angular/core';
import type { TreeEntity } from '../../core/models/entity.model';
import { EntityHelperService } from '../../core/services/entity-helper.service';
import { MessagePropertyKey } from '../../core/constants/message-property-keys';
import type { EntityType } from '../../core/models/entity-type.model';
import { MetadataKeys } from '../../shared/constants/metadata-keys';
import { EntityTypeKey } from '../../shared/constants/entity-type-keys';

@Injectable({
  providedIn: 'root'
})
export class ProductMessagesService {
  constructor(private entityHelper: EntityHelperService) {}

  /**
   * Returns boolean values for whether the provided product contains any messages or notes
   * @param product The product to check
   * @param entityTypes Entity types dictionary, needed to check for entity note properties from metadata
   */
  public getProductMessageStatuses(product: TreeEntity, entityTypes: Record<string, EntityType>): ProductMessageStatuses {
    const messages = this.entityHelper
      .getEntitiesOfTypeFromEntityTree(product, EntityTypeKey.Message)
      .filter(
        message =>
          message.Parent?.EntityTypeKey !== EntityTypeKey.GastroFlowHoodLine &&
          message.Parent?.EntityTypeKey !== EntityTypeKey.GastroFlowHoodConnectionLine
      );
    const messageLevels = messages.map(m => m.Properties[MessagePropertyKey.Level].Value as number);

    const hasErrors = messageLevels.some(l => l === 0);
    const hasWarnings = messageLevels.some(l => l === 1);
    let hasInfo = messageLevels.some(l => l === 2);

    // If there's no messages, check if there's info properties with values in the entity
    if (!hasInfo && entityTypes) {
      hasInfo = this.entityHelper.entityTreeContainsMetaDataFlag(product, entityTypes, MetadataKeys.propertyLevelInfo);
    }

    return {
      hasInfo,
      hasWarnings,
      hasErrors
    };
  }
}

export interface ProductMessageStatuses {
  hasErrors: boolean;
  hasWarnings: boolean;
  hasInfo: boolean;
}
